import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { EdgeProps, EdgeLabelRenderer, getSimpleBezierPath, Position } from '@xyflow/react';
import okBadge from '../../assets/ok.png';
import firewallBadge from '../../assets/firewall.png';
import './WorkflowEdge.css';
import WorkflowEdgePopOver from './WorkflowEdgePopover';
import { getNextZIndex } from './globalZIndex';

interface WorkflowEdgeData {
  hasVulnerability?: boolean;
  vulnerabilityCount?: number;
  guardrails?: string[];
  edgeIndex: number;
  curveOffset?: number; //offset for bidirectional edges
}

const WorkflowEdge: React.FC<EdgeProps<WorkflowEdgeData>> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition = Position.Right,
  targetPosition = Position.Left,
  data,
  markerEnd,
  markerStart,
  selected,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [popoverPos, setPopoverPos] = useState({ x: 0, y: 0 });
  const badgeRef = useRef<HTMLDivElement>(null);
  const [isClicked, setIsClicked] = useState(false);
  const isPopoverOpen = isHovered || isClicked;
  const [zIndex, setZIndex] = useState(9999);
  const curveOffset = data?.curveOffset ?? 0;

  const hasVulnerability = data?.hasVulnerability ?? false;
  const vulnerabilityCount = data?.vulnerabilityCount ?? 0;
  const guardrails = data?.guardrails ?? [];
  const edgeIndex = data?.edgeIndex ?? 0;
  
  const midX = (sourceX + targetX) / 2;
  const midY = (sourceY + targetY) / 2;

  // For bidirectional edges: use custom curve with offset
  // For single edges: use ReactFlow's natural bezier curve
  let edgePath: string;
  let finalLabelX: number;
  let finalLabelY: number;

  if (curveOffset !== 0) {
    // Bidirectional: custom quadratic bezier with offset
    const controlY = midY + curveOffset;
    edgePath = `M ${sourceX} ${sourceY} Q ${midX} ${controlY} ${targetX} ${targetY}`;
    
    // Label position on the curve
    const t = 0.5;
    finalLabelX = (1-t)*(1-t)*sourceX + 2*(1-t)*t*midX + t*t*targetX;
    finalLabelY = (1-t)*(1-t)*sourceY + 2*(1-t)*t*controlY + t*t*targetY;
  } else {
    // Single edge: use ReactFlow's natural bezier
    const [path, labelX, labelY] = getSimpleBezierPath({
      sourceX,
      sourceY,
      targetX,
      targetY,
      sourcePosition,
      targetPosition,
    });
    edgePath = path;
    
    finalLabelX = labelX;
    finalLabelY = labelY;
  }
  
  const strokeColor = hasVulnerability ? '#F86060' : '#49495B';
  const opacity = hasVulnerability ? 0.6 : 1;

  const handleMouseEnter = useCallback(() => {
    if (badgeRef.current) {
      const rect = badgeRef.current.getBoundingClientRect();
      setPopoverPos({ x: rect.left + rect.width / 2, y: rect.top - 10 });
    }
    setZIndex(getNextZIndex());
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  const handleClose = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsClicked(false);
    setIsHovered(false);
  }, []);

  const handleBadgeClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (badgeRef.current) {
      const rect = badgeRef.current.getBoundingClientRect();
      setPopoverPos({ x: rect.left + rect.width / 2, y: rect.top - 10 });
    }
    setZIndex(getNextZIndex());
    setIsClicked(true);
  }, []);

  useEffect(() => {
    if (isPopoverOpen && badgeRef.current) {
      const rect = badgeRef.current.getBoundingClientRect();
      setPopoverPos({ x: rect.left + rect.width / 2, y: rect.top - 10 });
    }
  }, [isPopoverOpen, sourceX, sourceY, targetX, targetY]);

  return (
    <>
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={20}
        className="workflow-edge-hover-path"
      />
      
      <path
        d={edgePath}
        fill="none"
        stroke={selected? '#0da4bfff' : strokeColor}
        strokeWidth={selected? 3 : 1.5}
        strokeDasharray={selected ? undefined : '5,5'}
        opacity={selected? 1 : opacity}
        markerEnd={markerEnd}
        markerStart={markerStart}
        className={selected ? 'workflow-edge-selected' : ''}
      />

      <EdgeLabelRenderer>
        <div
          ref={badgeRef}
          className={`workflow-edge-badge ${isHovered ? 'workflow-edge-badge--active' : ''}`}
          style={{ transform: `translate(-50%, -50%) translate(${finalLabelX}px, ${finalLabelY}px)` }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={handleBadgeClick}
        >
          {hasVulnerability ? (
            <div className="workflow-edge-badge-vulnerability">
              <img 
                src={firewallBadge} 
                alt="Vulnerability" 
                className="workflow-edge-badge-vulnerability-icon"
              />
              <span className="workflow-edge-badge-vulnerability-count">
                {vulnerabilityCount}
              </span>
            </div>
          ) : (
            <div className="workflow-edge-badge-ok">
              <img 
                src={okBadge} 
                alt="OK" 
                className="workflow-edge-badge-ok-icon"
              />
            </div>
          )}
        </div>
      </EdgeLabelRenderer>

      {isPopoverOpen && createPortal(
        <WorkflowEdgePopOver
          isOpen={isPopoverOpen}
          position={popoverPos}
          zIndex={zIndex}
          onClose={handleClose}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          hasVulnerability={hasVulnerability}
          vulnerabilityCount={vulnerabilityCount}
          guardrails={guardrails}
        />,
        document.body
      )}
    </>
  );
};

export default WorkflowEdge;